import type {HasId, HasUuid} from "~/repository/types";

import {type ClassValue, clsx} from 'clsx'
import {twMerge} from 'tailwind-merge'
import type {Updater} from "@tanstack/vue-table";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
    ref.value = typeof updaterOrValue === 'function'
        ? updaterOrValue(ref.value)
        : updaterOrValue
}

export const {format: formatNumber} = Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1
})

export const groupById = <T extends HasId>(items: Array<T>, idCallback: ((id: number) => number | string) | undefined = undefined) => {
    return items.reduce(
        (list: any, item: T) => {
            if (idCallback) {
                list[idCallback(item.id)] = item
            } else {
                list[item.id] = item
            }
            return list
        }, {}
    )
}

export const groupByUuid = <T extends HasUuid>(items: Array<T>, idCallback: ((id: string) => string) | undefined = undefined) => {
    return items.reduce(
        (list: any, item: T) => {
            const id = idCallback ? idCallback(item.uuid) : item.uuid;
            list[id] = item;
            return list;
        }, {}
    );
}


export const formatDate = (dateString: string, locale: string) => {
    const date = new Date(dateString)
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) - 1;

    const options = {timeZone: 'UTC'}; // Enforce UTC time zone for consistency

    if (diffDays === 0) {
        return date.toLocaleTimeString(locale, {hour: 'numeric', minute: '2-digit', ...options}); // Today, show time
    } else if (diffDays === 1) {
        return new Date().toLocaleDateString(locale, {weekday: 'long', ...options}); // Yesterday
    } else if (diffDays < 7) {
        return date.toLocaleDateString(locale, {weekday: 'long', ...options}); // Within a week, show day name
    } else {
        return date.toLocaleDateString(locale, {month: 'short', day: 'numeric', year: 'numeric', ...options}); // Otherwise, show month, day, year
    }
}


export const getDomainFromUrl = (url: string | null | undefined) => {
    if (!url) return '';


    let domain = url.replace(/^https?:\/\//i, '');


    domain = domain.replace(/\/.*$/, '');


    const parts = domain.split('.');
    if (parts.length > 2) {
        domain = parts.slice(-2).join('.');
    }

    return domain;
}


export const getTimeAgoString = (timeString: string) => {
    const dateTime = new Date(timeString);
    const now = new Date();
    const diffInMilliseconds = now.getTime() - dateTime.getTime();
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);

    const units = [
        {value: 31536000, label: 'year'},
        {value: 2592000, label: 'month'},
        {value: 604800, label: 'week'},
        {value: 86400, label: 'day'},
        {value: 3600, label: 'hour'},
        {value: 60, label: 'minute'},
        {value: 1, label: 'second'}
    ];

    for (let i = 0; i < units.length; i++) {
        const unit = units[i];
        const value = Math.floor(diffInSeconds / unit.value);
        if (value >= 1) {
            return `${value} ${unit.label}${value !== 1 ? 's' : ''}`;
        }
    }

    return 'Just now';
}